/**
 * İstemci değişiklik katmanı (2026-10-17) — üç uygulamanın (web / organizer / admin) TÜM `/api/*`
 * istekleri buradan geçer (`installMutationFetch()` `window.fetch`'i sarar). Sunucudaki değişiklik
 * kapısının (`mutationGate`) istemci yarısı; piyasa karşılığı Stripe `Idempotency-Key` ve Google
 * AIP-154 / RFC 9110 `If-Match`:
 *
 *  - Her değişiklik isteğine (POST/PUT/PATCH/DELETE) benzersiz `Idempotency-Key` — aynı isteğin ağ
 *    düzeyinde tekrarı sunucuda bir kez işlenir (409 `duplicate_request`).
 *  - Aynı anda uçuşta olan BİREBİR aynı değişiklik (yöntem + adres + gövde) tek istek olur: çift tık,
 *    iki kez gönderilen form sunucuya bir kez gider, iki çağıran aynı yanıtı alır.
 *  - Okuma yanıtı `X-Etags: 1` taşıyorsa belgelerin sürümleri saklanır; o belgeye PUT/PATCH yazılırken
 *    `If-Match` otomatik eklenir. Sunucu 409 `stale_write` dönerse (belge bu arada başka yerden değişti)
 *    `api:stale-write` olayı yayılır → uygulama verisini tazeler ve kullanıcıya söyler.
 *
 * Yalnız aynı kökene (same-origin) giden `/api/` istekleri etkilenir; R2 imzalı yüklemeler, üçüncü
 * taraf adresler olduğu gibi geçer. Dosya/akış gövdeleri birleştirilmez (karşılaştırılamaz).
 */

export const STALE_WRITE_EVENT = "api:stale-write";
export const ETAGS_RESPONSE_HEADER = "X-Etags";

const MUTATING = new Set(["POST", "PUT", "PATCH", "DELETE"]);
/** Belge yazımları — sürüm (If-Match) yalnız bunlara eklenir. */
const VERSIONED = new Set(["PUT", "PATCH"]);

type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

export interface MutationFetchOptions {
  /** İsteklerin gittiği köken (tarayıcıda `location.origin`). */
  origin: string;
  /** Belge başka yerden değiştiği için yazım reddedildi (409 `stale_write`). */
  onStale?: (path: string) => void;
  /** Test için: anahtar üreteci. */
  newKey?: () => string;
}

const randomKey = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `k${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;

/** Sunucu sürümleri sonda `/` olmadan anahtarlar (Hono'da sondaki `/` 404'tür). */
const docPath = (pathname: string) => (pathname.length > 1 ? pathname.replace(/\/$/, "") : pathname);

export function createMutationFetch(base: FetchLike, opts: MutationFetchOptions) {
  const etags = new Map<string, string>();
  const inflight = new Map<string, Promise<Response>>();
  const newKey = opts.newKey ?? randomKey;

  const remember = async (res: Response) => {
    try {
      const body = (await res.clone().json()) as { etags?: Record<string, unknown> } | null;
      for (const [path, tag] of Object.entries(body?.etags ?? {})) {
        if (typeof tag === "string") etags.set(docPath(path), tag);
      }
    } catch {
      /* JSON değil — sürüm yok */
    }
  };

  const afterWrite = async (path: string, res: Response) => {
    if (res.ok) {
      // Yanıt yeni sürümü taşıyorsa saklanır; taşımıyorsa eski sürüm artık geçersiz (belge değişmiş olabilir).
      if (res.headers.get(ETAGS_RESPONSE_HEADER) === "1") await remember(res);
      else etags.delete(path);
      return;
    }
    if (res.status !== 409) return;
    try {
      const body = (await res.clone().json()) as { error?: string } | null;
      if (body?.error === "stale_write") {
        etags.delete(path);
        opts.onStale?.(path);
      }
    } catch {
      /* JSON değil */
    }
  };

  const wrapped: FetchLike = async (input, init) => {
    const raw = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    let url: URL;
    try {
      url = new URL(raw, opts.origin);
    } catch {
      return base(input, init);
    }
    if (url.origin !== opts.origin || !url.pathname.startsWith("/api/")) return base(input, init);

    const method = (init?.method ?? (typeof input === "object" && "method" in input ? input.method : "GET")).toUpperCase();
    const path = docPath(url.pathname);

    if (!MUTATING.has(method)) {
      const res = await base(input, init);
      if (res.headers.get(ETAGS_RESPONSE_HEADER) === "1") await remember(res);
      return res;
    }

    const isRequest = typeof input === "object" && !(input instanceof URL);
    const headers = new Headers(init?.headers ?? (isRequest ? (input as Request).headers : undefined));
    if (!headers.has("Idempotency-Key")) headers.set("Idempotency-Key", newKey());
    if (VERSIONED.has(method) && !headers.has("If-Match")) {
      const tag = etags.get(path);
      if (tag) headers.set("If-Match", tag);
    }

    // Birleştirme yalnız karşılaştırılabilir gövdede (metin / boş) ve gövdesi init'te olan çağrıda.
    const body = init?.body;
    const comparable = !isRequest && (body === undefined || body === null || typeof body === "string");
    const key = comparable ? `${method} ${url.pathname}${url.search}\n${body ?? ""}` : null;
    if (key) {
      const running = inflight.get(key);
      if (running) return running.then((r) => r.clone());
    }

    const run = (async () => {
      const res = await base(input, { ...init, headers });
      await afterWrite(path, res);
      return res;
    })();
    if (!key) return run;
    inflight.set(key, run);
    run.then(
      () => inflight.delete(key),
      () => inflight.delete(key),
    );
    // Her çağırana kopya: ilk okuyan gövdeyi tüketse de diğerleri okuyabilsin.
    return run.then((r) => r.clone());
  };

  return Object.assign(wrapped, {
    /** Test / tanılama: saklanan sürüm. */
    etagFor: (path: string) => etags.get(docPath(path)),
  });
}

const INSTALLED = Symbol.for("wedding.mutationFetch");

/** `window.fetch`'i değişiklik katmanıyla sarar (bir kez; HMR'de tekrar sarmaz). */
export function installMutationFetch(): void {
  if (typeof window === "undefined" || typeof window.fetch !== "function") return;
  const w = window as Window & { [INSTALLED]?: boolean };
  if (w[INSTALLED]) return;
  w[INSTALLED] = true;
  window.fetch = createMutationFetch(window.fetch.bind(window), {
    origin: window.location.origin,
    onStale: (path) => window.dispatchEvent(new CustomEvent(STALE_WRITE_EVENT, { detail: { path } })),
  });
}

/** `api:stale-write` dinleyicisi; aboneliği kaldıran fonksiyonu döner. */
export function onStaleWrite(handler: (path: string) => void): () => void {
  if (typeof window === "undefined") return () => {};
  const listener = (e: Event) => handler(String((e as CustomEvent<{ path?: string }>).detail?.path ?? ""));
  window.addEventListener(STALE_WRITE_EVENT, listener);
  return () => window.removeEventListener(STALE_WRITE_EVENT, listener);
}
